import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { getAuthHeaders, handleAuthError } from './config';

/*
 * Truthful load states for the wedding planner.
 *
 * A single `loading` boolean cannot tell "we have nothing because the plan is
 * empty" apart from "we have nothing because the request failed", so a failed
 * fetch used to fall through to the success empty state — an outage looked
 * exactly like erased planning and financial data. These helpers model the
 * three states that actually exist:
 *
 *   phase 'loading'                 → never loaded, first attempt in flight
 *   phase 'error'  + data === null  → never loaded, the request failed
 *   phase 'ready'                   → loaded from a successful response
 *   phase 'error'  + data !== null  → loaded once, a later refresh failed
 *                                     (the last trusted data is kept)
 *
 * Empty-state copy and any derived totals may only be rendered in the 'ready'
 * phase or from retained data — never invented while `data` is null.
 */

// Returned by `fetchJson` when `handleAuthError` has started the redirect to
// /login. The caller must stop rather than record an error: the page is
// already navigating away, and an error panel would flash over the redirect.
export const AUTH_REDIRECT = Symbol('auth-redirect');

export class RequestError extends Error {
  constructor(kind, userMessage, status) {
    super(userMessage);
    this.name = 'RequestError';
    this.kind = kind; // 'network' | 'http' | 'parse'
    this.status = status ?? null;
  }
}

// Path only — never the query string, and never the headers, which carry the
// X-API-Key. Enough to identify the failing call without leaking credentials.
const pathOf = (url) => {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return String(url);
  }
};

const logFailure = (label, url, detail) => {
  console.error(`[wedding] ${label} request failed`, { path: pathOf(url), ...detail });
};

/**
 * GET a JSON endpoint with auth headers, converting every failure mode into a
 * RequestError carrying copy we are willing to show a human.
 */
export async function fetchJson(url, { label = 'data' } = {}) {
  let response;
  try {
    response = await fetch(url, { headers: getAuthHeaders() });
  } catch (networkError) {
    logFailure(label, url, { kind: 'network', message: networkError?.message });
    throw new RequestError('network', 'We could not reach the server. Check your connection and try again.');
  }

  // 401 keeps its existing behaviour: clear the token and redirect to /login.
  if (handleAuthError(response)) return AUTH_REDIRECT;

  if (!response.ok) {
    logFailure(label, url, { kind: 'http', status: response.status, statusText: response.statusText });
    const message = response.status >= 500
      ? `The server could not return your ${label} (error ${response.status}).`
      : `The request for your ${label} was rejected (error ${response.status}).`;
    throw new RequestError('http', message, response.status);
  }

  try {
    return await response.json();
  } catch (parseError) {
    logFailure(label, url, { kind: 'parse', message: parseError?.message });
    throw new RequestError('parse', `The server sent an unexpected response for your ${label}.`);
  }
}

/**
 * Holds one remote resource in the three-state model described above.
 *
 * @param loader  async () => data | AUTH_REDIRECT
 * @returns { data, phase, error, busy, hasData, isStale, failedFirstLoad, load, setData }
 */
export function useRemoteData(loader) {
  const [state, setState] = useState({ data: null, phase: 'loading', error: null, loadedAt: null });
  const [busy, setBusy] = useState(true);
  // Guards against a slow first request resolving after a fast retry and
  // overwriting the newer result.
  const requestSeq = useRef(0);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setBusy(true);
    try {
      const data = await loader();
      if (data === AUTH_REDIRECT) return; // redirecting to /login; leave state alone
      if (!mounted.current || seq !== requestSeq.current) return;
      setState({ data, phase: 'ready', error: null, loadedAt: Date.now() });
    } catch (error) {
      if (!mounted.current || seq !== requestSeq.current) return;
      // Keep `data` — a refresh failure must not discard trusted data.
      setState((prev) => ({ ...prev, phase: 'error', error }));
    } finally {
      if (mounted.current && seq === requestSeq.current) setBusy(false);
    }
  }, [loader]);

  // Lets a mutation response replace the data without a round trip.
  const setData = useCallback((data) => {
    requestSeq.current += 1;
    setState({ data, phase: 'ready', error: null, loadedAt: Date.now() });
  }, []);

  return {
    data: state.data,
    phase: state.phase,
    error: state.error,
    loadedAt: state.loadedAt,
    busy,
    hasData: state.data !== null,
    // Loaded once, then a refresh failed: show the data with a stale notice.
    isStale: state.phase === 'error' && state.data !== null,
    // Never loaded and the request failed: show the error panel, nothing else.
    failedFirstLoad: state.phase === 'error' && state.data === null,
    load,
    setData,
  };
}

const errorText = (error, fallback) =>
  (error && error.message) || fallback || 'Something went wrong.';

// ─── presentational: never-loaded failure ────────────────────────────────────

export function DataErrorPanel({ title, error, onRetry, retrying, fallbackMessage }) {
  return (
    <div
      role="alert"
      className="rounded-[30px] border border-red-200 bg-red-50/80 p-6 text-center shadow-sm sm:p-10"
    >
      <AlertTriangle className="mx-auto h-8 w-8 text-red-500" aria-hidden="true" />
      <h2 className="mt-3 text-base font-semibold text-red-900">{title}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-red-800">
        {errorText(error, fallbackMessage)}
      </p>
      <p className="mx-auto mt-2 max-w-md text-xs leading-5 text-red-700">
        Nothing has been lost — this page just could not read it. Your saved plan is untouched.
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="mt-5 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-70"
      >
        {retrying
          ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          : <RefreshCw className="h-4 w-4" aria-hidden="true" />}
        {retrying ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  );
}

// ─── presentational: loaded-then-refresh-failed ──────────────────────────────

const formatTime = (timestamp) => {
  if (!timestamp) return null;
  try {
    return new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return null;
  }
};

export function StaleDataNotice({ label, error, onRetry, retrying, loadedAt, fallbackMessage }) {
  const at = formatTime(loadedAt);
  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-4 flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-left sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        <div>
          <p className="text-sm font-semibold text-amber-900">
            Showing your last saved {label}{at ? `, loaded at ${at}` : ''}.
          </p>
          <p className="mt-0.5 text-xs leading-5 text-amber-800">
            {errorText(error, fallbackMessage)} It may be out of date, and edits may not save until this clears.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-full bg-amber-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-800 disabled:opacity-70"
      >
        {retrying
          ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          : <RefreshCw className="h-4 w-4" aria-hidden="true" />}
        {retrying ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  );
}

// ─── presentational: first-load spinner ──────────────────────────────────────

export function LoadingPanel({ label }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-center gap-3 rounded-[30px] bg-white/75 p-16 shadow-sm ring-1 ring-stone-200"
    >
      <Loader2 className="h-7 w-7 animate-spin text-stone-500" aria-hidden="true" />
      <span className="text-sm font-medium text-stone-500">Loading {label}…</span>
    </div>
  );
}
